import { DateTime } from 'luxon';

import {
  ProgramInvoiceDataFragment,
  ProgramInvoiceStatus,
} from '../../../generated/graphql';
import { camelCaseToCapitalizedWords } from '../../lib/string';
import { USER_LOCAL_TIMEZONE } from '../../lib/time';

export const INVOICE_EXPORT_HEADERS = [
  'Amount',
  'Currency',
  'Status',
  'Invoice number',
  'Client',
  'Client email',
  'Created by',
  'Created by email',
  'Created',
  'Due',
] as const;

const escapeCsvValue = (value: string | number): string => {
  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const formatInvoiceExportDate = (timestamp?: string | null): string => {
  if (!timestamp) {
    return '';
  }
  const dateTime = DateTime.fromISO(timestamp).setZone(USER_LOCAL_TIMEZONE);
  return dateTime.isValid ? dateTime.toFormat('yyyy-MM-dd') : '';
};

export const formatInvoiceExportStatus = (
  status: ProgramInvoiceStatus | string,
): string => camelCaseToCapitalizedWords(status);

export const generateInvoicesExportFilename = (
  exportedAt = new Date(),
): string => {
  const timestamp = DateTime.fromJSDate(exportedAt).toFormat('yyyy-MM-dd');
  return `invoices-export_${timestamp}.csv`;
};

export const buildInvoicesCsv = (
  invoices: ProgramInvoiceDataFragment[],
): string => {
  const rows = invoices.map((invoice) =>
    [
      invoice.amount,
      invoice.currency.toUpperCase(),
      formatInvoiceExportStatus(invoice.status),
      invoice.stripeInvoiceNumber,
      invoice.patient?.name ?? '',
      invoice.patient?.email ?? '',
      invoice.createdByProviderUser?.name ?? '',
      invoice.createdByProviderUser?.email ?? '',
      formatInvoiceExportDate(invoice.createdAt),
      formatInvoiceExportDate(invoice.dueAt),
    ]
      .map(escapeCsvValue)
      .join(','),
  );

  return `\uFEFF${[INVOICE_EXPORT_HEADERS.join(','), ...rows].join('\n')}\n`;
};

export const downloadInvoicesCsv = (
  invoices: ProgramInvoiceDataFragment[],
): void => {
  const csv = buildInvoicesCsv(invoices);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = generateInvoicesExportFilename();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
